/**
 * `LlmsEditorDrawer` — right-side drawer that mounts the full
 * `SettingsLlmsTxt` editor inline on the Agent Readiness page.
 *
 * The AI-Discovery Settings tab is retired (2026-10-01); the editor's
 * single remaining entry point is the "Edit llms.txt" chip in the
 * Discoverable section (and the "Open llms.txt editor" button inside
 * the fix drawer for the `llms_txt.missing` step).
 *
 * Round-3 bug fix (2026-10-01):
 *   The drawer previously handed the `site` object from `SiteCtx` to
 *   `SettingsLlmsTxt`. `GET /v1/sites` (the list route the SiteCtx
 *   bootstrap uses) does NOT return `llms_description` or
 *   `llms_custom_sections` — those live on `GET /v1/sites/:id`. The
 *   editor opened empty on sites that had a saved description.
 *   Fix: fetch the site detail on open and only render the editor
 *   once the detail arrives. A skeleton holds the drawer open while
 *   it loads; save is impossible before load (no editor renders at
 *   all).
 */

import { useEffect, useState } from 'react';

import type { Site } from '@sill/types';

import { Drawer } from '../../components/atoms/Drawer.js';
import { SettingsLlmsTxt } from '../settings/SettingsLlmsTxt.js';
import { useApi } from '../../api/api-context.js';
import { useC } from '../../lib/theme.js';

export interface LlmsEditorDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly site: Site;
}

export function LlmsEditorDrawer({ open, onClose, site }: LlmsEditorDrawerProps): JSX.Element | null {
  const api = useApi();
  const C = useC();
  const [detail, setDetail] = useState<Site | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset per-open so a merchant switching sites while the drawer is
  // closed doesn't see a stale detail from the previous one.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setDetail(null);
    setError(null);
    void api.sites.get(site.site_id).then(
      (fresh) => {
        if (cancelled) return;
        if (fresh === null) {
          // The site exists in the list bootstrap but the detail
          // route returned 404 — fall back to the list copy so the
          // editor renders *something*, but the save path still
          // revalidates via If-Match on the server side.
          setDetail(site);
          return;
        }
        setDetail(fresh);
      },
      () => {
        if (cancelled) return;
        setError('Could not load llms.txt values. Try again in a moment.');
      },
    );
    return () => { cancelled = true; };
    // `open` + `site.site_id` are the right dependencies — a new open
    // event or a different active site both re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, site.site_id]);

  if (!open) return null;
  return (
    <Drawer open={open} onClose={onClose} title="LLMs.TXT · EDITOR" data-testid="readiness-llms-drawer">
      <div className="p-4 sm:p-6">
        {error !== null ? (
          <div
            role="alert"
            data-testid="readiness-llms-drawer-error"
            style={{
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4,
              padding: 16, color: C.body, fontSize: 14,
            }}
          >
            {error}
          </div>
        ) : detail === null ? (
          <LlmsEditorSkeleton />
        ) : (
          <SettingsLlmsTxt site={detail} />
        )}
      </div>
    </Drawer>
  );
}

/**
 * Skeleton while `api.sites.get(site_id)` is in flight — holds the
 * layout open and makes it obvious the editor is loading, not empty.
 * Three rows + a preview block, each 1px-border and surface-tinted so
 * the drawer doesn't flicker on load.
 */
function LlmsEditorSkeleton(): JSX.Element {
  const C = useC();
  const row = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 10,
    padding: '14px 0',
    borderBottom: `1px solid ${C.borderSoft}`,
  } as const;
  const bar = (w: string | number): JSX.Element => (
    <div
      aria-hidden="true"
      style={{
        height: 14, width: w, background: C.surface2, borderRadius: 2,
      }}
    />
  );
  return (
    <div data-testid="readiness-llms-drawer-skeleton" aria-busy="true" aria-live="polite" style={{ fontFamily: "'Geist', sans-serif", color: C.muted }}>
      <div style={{ marginBottom: 16, fontSize: 12 }}>Loading your current llms.txt…</div>
      <div style={row}>{bar(120)}{bar('100%')}</div>
      <div style={row}>{bar(140)}{bar('100%')}</div>
      <div style={row}>{bar(100)}<div style={{ height: 180, background: C.surface2, borderRadius: 2 }} /></div>
    </div>
  );
}
